/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment, useEffect, useRef, useState } from 'react'

import { createAnalytics } from '@elastic/ebt/client'

import type { MiddlewareComponent } from '@modules/app/types'
import { useConfig } from '@modules/cui/ConfigContext'

import ConfigMiddleware from '../app/middleware/ConfigMiddleware'

import EbtContext from './EbtContext'
import { LoggerFactory } from './logger'
import { EbtAnalytics } from './EbtAnalytics'

const EbtMiddleware: MiddlewareComponent = ({ children }) => {
  const [isEbtInit, setEbtInit] = useState<boolean>(false)
  const isDevEnv = (e) => e === 'qa' || e === 'staging'
  const env = useConfig('CLOUD_UI_ENV')
  const channelName = useConfig('EBT_CHANNEL')
  const logger = LoggerFactory('ebt')

  const [analytics, setAnalytics] = useState<ReturnType<typeof createAnalytics> | null>(null)
  const [analyticsInstance, setAnalyticsInstance] = useState<EbtAnalytics | null>(null)
  const initializedRef = useRef(false)

  useEffect(() => {
    if (!initializedRef.current) {
      const _analytics = createAnalytics({
        // Set to `true` when running in developer mode.
        isDev: isDevEnv(env),
        // The application's instrumented logger TODO we have to add a logger
        // @ts-ignore
        logger,
      })
      const _analyticsInstance = new EbtAnalytics(_analytics)
      setAnalytics(_analytics)
      setAnalyticsInstance(_analyticsInstance)
      initializedRef.current = true
    }
  }, [env, logger])

  if (channelName) {
    return (
      <EbtContext.Provider
        value={{
          analytics: analytics ?? undefined,
          analyticsInstance: analyticsInstance ?? undefined,
          isEbtInit,
          setEbtInit,
        }}
      >
        {children}
      </EbtContext.Provider>
    )
  }

  return <Fragment>{children}</Fragment>
}

EbtMiddleware.middleware = {
  displayName: 'EbtMiddleware',
  dependencies: [ConfigMiddleware],
}

export default EbtMiddleware
